import type { ReactNode } from "react";
import { RelatedLink } from "@/components/RelatedPages";
import { SITE_URL, ORG_ID } from "@/components/schemaConstants";

export { SITE_URL, ORG_ID };

/* ── Types ── */

export interface LearnFAQ {
  question: string;
  answer: string;
}

export interface LearnSection {
  heading: string;
  body: ReactNode;
}

export interface LearnSource {
  citation: string;
  url?: string;
}

export interface LearnReviewBlock {
  writtenBy?: string;
  reviewedBy?: string;
  reviewedByHref?: string;
  published?: string;
  lastReviewed?: string;
  lastUpdated?: string;
}

export interface TOCItem {
  label: string;
  href: string;
}

export interface CTABand {
  headline: string;
  primaryText: string;
  primaryHref: string;
  secondaryText: string;
  secondaryHref: string;
  phone: string;
}

export interface LearnPageData {
  slug: string;
  title: string; // <title> (without "| Neuro Wellness TMS" suffix — added by component)
  description: string; // meta description
  canonical: string; // path, no trailing slash
  h1: string;
  breadcrumbLabel: string;
  crumbs: { name: string; href: string }[];
  shortAnswer: string;
  keyTakeaways: string[];
  sections: LearnSection[];
  faqs: LearnFAQ[];
  sources: LearnSource[];
  schemaJson: object; // full @graph JSON-LD
  relatedLinks: RelatedLink[];
  reviewBlock?: LearnReviewBlock;
  toc?: TOCItem[];
  safetyBlock?: string;
  trademarkFooter?: string;
  ctaBand?: CTABand;
}

/* ── Shared org node (uses the live site's phone number) ── */

export const orgNode = {
  "@type": "MedicalOrganization",
  "@id": ORG_ID,
  name: "Neuro Wellness TMS Centers of America",
  url: `${SITE_URL}/`,
  telephone: "+19548279793",
  address: {
    "@type": "PostalAddress",
    streetAddress: "9734 W Sample Rd",
    addressLocality: "Coral Springs",
    addressRegion: "FL",
    postalCode: "33065",
    addressCountry: "US",
  },
  areaServed: [
    "Coral Springs, FL",
    "Broward County, FL",
    "Palm Beach County, FL",
    "Fort Lauderdale, FL",
    "Boca Raton, FL",
    "Miami, FL",
  ],
};

/* ── MedicalClinic node (full NAP for SAINT pages) ── */

export const medicalClinicNode = {
  "@type": "MedicalClinic",
  "@id": ORG_ID,
  name: "Neuro Wellness TMS Centers of America",
  url: SITE_URL,
  telephone: "+1-954-827-9793",
  address: {
    "@type": "PostalAddress",
    streetAddress: "9734 W Sample Rd, 2nd Floor, Suite B",
    addressLocality: "Coral Springs",
    addressRegion: "FL",
    postalCode: "33065",
    addressCountry: "US",
  },
};

/* ── Breadcrumb helpers ── */

export const baseCrumbs = (label: string) => [
  { name: "Home", href: "/" },
  { name: "Patient Education", href: "/learn" },
  { name: label, href: "" }, // filled by component with canonical
];

export function breadcrumbJsonld(crumbs: { name: string; href: string }[]) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.href
        ? `${SITE_URL}${c.href.startsWith("/") ? c.href : `/${c.href}`}`
        : undefined,
    })),
  };
}

/* ── MedicalWebPage node helper ── */

export function webPageNode(params: {
  id: string;
  name: string;
  url: string;
  description: string;
  about?: object | object[];
  reviewedBy?: { name: string; url?: string };
  lastReviewed?: string;
}) {
  const node: Record<string, unknown> = {
    "@type": "MedicalWebPage",
    "@id": params.id,
    name: params.name,
    url: params.url,
    description: params.description,
    inLanguage: "en-US",
    datePublished: "2026-11-03",
    dateModified: "2026-11-03",
    lastReviewed: params.lastReviewed || "2026-11-03",
    publisher: { "@id": ORG_ID },
    audience: { "@type": "MedicalAudience", audienceType: "Patient" },
  };
  if (params.about) node.about = params.about;
  if (params.reviewedBy) {
    node.reviewedBy = {
      "@type": "Person",
      name: params.reviewedBy.name,
      ...(params.reviewedBy.url ? { url: params.reviewedBy.url } : {}),
    };
  }
  return node;
}

/* ── FAQPage JSON-LD helper ── */

export function faqJsonld(faqs: LearnFAQ[], id: string) {
  return {
    "@type": "FAQPage",
    "@id": id,
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

/* ── Shared related links ── */

export const sharedRelated: RelatedLink[] = [
  {
    title: "SAINT® Accelerated TMS",
    href: "/saint",
    description: "The 5-day, fMRI-guided protocol offered at our clinic",
  },
  {
    title: "What Is TMS Therapy?",
    href: "/contents/tms/what-is-tms-therapy",
    description: "How transcranial magnetic stimulation works for depression",
  },
  {
    title: "Cost & Insurance",
    href: "/cost-insurance",
    description: "Benefits verification, CPT codes, and self-pay options",
  },
  {
    title: "Am I a Candidate?",
    href: "/am-i-a-candidate",
    description: "Screening factors for TMS and SAINT® eligibility",
  },
  {
    title: "Schedule a Consultation",
    href: "/contactus",
    description: "Book a free consultation with our clinical team",
  },
  {
    title: "Contact & Directions",
    href: "/contactus",
    description: "9734 W Sample Rd, Coral Springs, FL 33065",
  },
];
